import React from "react";
import { Box, Link, ListItem, Typography } from "@mui/material";
import CheckedCircleIcon from "../../../components/icon-components/CheckedCircleIcon";
import CircleOutlineIcon from "../../../components/icon-components/CircleOutlineIcon";
import PlayButtonIcon from "../../../components/icon-components/PlayButtonIcon";
import DocumentIcon from "../../../components/icon-components/DocumentIcon";
import styles from "../styles/CarePlanActivityItem.module.scss";
import { useTranslation } from "react-i18next";
import { ActivityData } from "../../../utility/interfaces/carePlanInterfaces";
import {
  CONTENT_STATUS_COMPLETED_ID,
  SAVE_CLICK_STATUS_ID,
} from "../../../utility/constants/common-constants";
import {
  getPatientRoute,
  handleExternalLinkClick,
} from "../../../utility/functions/common-functions";
import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import { useSaveContentStatusMutation } from "../../view-learning-content/services/SaveContentStatusAPISlice";
import { setLearningContent } from "../../view-learning-content/services/LearningContentDataSlice";
import { useLocation, useNavigate } from "react-router-dom";
import { useSaveContentClickMutation } from "../../view-learning-content/services/SaveContentStatusAccessLogAPISlice";
import NoImageLearningIcon from "../../../components/icon-components/NoImageLearningIcon";

interface Props {
  activity: ActivityData;
  isLast: boolean;
}

const CarePlanActivityItem: React.FC<Props> = ({ activity, isLast }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const isCompleted = activity.activityCompletionStatusCode === "Completed";
  const isVideo = activity.contentTypeCode === "videos";
  const isArticle = activity.contentTypeCode === "articles";
  const isURL = activity.contentTypeCode === "URL";

  const thumbnailUrl = activity.learningMetaData?.thumbnail;
  const length = activity.learningMetaData?.length;
  const hasMedia = isVideo || isArticle;

  const [saveContentClick] = useSaveContentClickMutation();
  const [contentStatus] = useSaveContentStatusMutation();

  const {
    preferredLanguage: lang,
    patientProfileId,
    userName: patientUserName,
  } = useAppSelector((state) => state.patientProfileDetails.patientProfileInfo);

  // Click to view content

  const showContetntDetails = async (content: ActivityData) => {
    // Handle external link content as before
    if (content?.contentTypeCode === "URL") {
      if (content?.milestoneLearningUrls) {
        window.open(
          content?.milestoneLearningUrls,
          "_blank",
          "noopener,noreferrer"
        );
        if (content?.activityCompletionStatus !== t("completed")) {
          handleExternalLinkClick(
            content.casePlanMilestoneMappingId,
            CONTENT_STATUS_COMPLETED_ID,
            patientUserName || "Patient",
            patientProfileId,
            lang,
            dispatch,
            contentStatus
          );
        }
      }
    } else {
      // Dispatch content to Redux
      dispatch(
        setLearningContent({
          type: content?.contentTypeCode,
          category: content?.activitySubCategoryName,
          videoUrl: content?.learningMetaData?.videoUrl,
          fullHtml: content?.learningMetaData?.fullHtml,
          title: content?.activityDisplayName,
          summarry: content?.learningMetaData?.summary,
          casePlanMilestoneMappingId: content?.casePlanMilestoneMappingId,
          activityCompletionStatus: content?.activityCompletionStatus,
          patientProfileId,
          lang: lang,
          activityPoints: content?.activityPoints,
          thumbnail: content?.learningMetaData?.thumbnail,
          length: content?.learningMetaData?.length,
          educationalContentSourceId: content?.educationalContentSourceId,
          educationalContentIdentifier: content?.contentCode.toString(),
          navigationInfo: {
            isViewLearningContentEnabled: true,
            arrivedFrom: location.pathname
          }
        })
      );

      // Navigate to the learning view
      navigate(getPatientRoute(patientProfileId, "/learning"), {
        state: {
          selectedTabId: 5,
        },
      });
    }

    // Save click mutation
    try {
      const body = {
        casePlanMilestoneMappingId: content?.casePlanMilestoneMappingId || "",
        patientProfileId: patientProfileId,
        planId: 1,
        caseId: 1,
        milestoneId: 1,
        contentIdentifier: content?.contentCode.toString(),
        contentWatchedDurationInMins: 1,
        action: SAVE_CLICK_STATUS_ID,
        educationalContentSourceId: content?.educationalContentSourceId,
      };

      await saveContentClick({
        body,
        lang,
      }).unwrap();
    } catch (err: any) {
      console.log(err, "saveContentClick");
    }
  };

  return (
    <ListItem
      disablePadding
      className={!isLast ? styles.activityItem : styles.lastActivityItem}
    >
      <Box display="flex" flex={1} gap={2} alignItems="center">
        {hasMedia && (
          <Box
            className={styles.thumbnail}
            onClick={() => showContetntDetails(activity)}
          >
            {thumbnailUrl ? (
              <img
                src={thumbnailUrl}
                alt={activity.activityDisplayName}
                width="60"
                height="60"
                className={styles.thumbnailImage}
              />
            ) : (
              <Box className={styles.thumbnailPlaceholder}>
                <NoImageLearningIcon width={24} height={24} />
              </Box>
            )}
            <Box className={styles.thumbnailIcon}>
              {isVideo ? <PlayButtonIcon /> : <DocumentIcon />}
            </Box>
          </Box>
        )}

        {/* Text */}
        <Box>
          <Typography variant="body2">
            {(isVideo || isArticle) && (
              <Box
                component="span"
                className={styles.linkText}
                onClick={() => showContetntDetails(activity)}
              >
                {isVideo ? t("carePlan.watch") : t("carePlan.read")}
              </Box>
            )}
            {isURL && activity.milestoneLearningUrls ? (
              <Link
                className={styles.linkText}
                onClick={() => showContetntDetails(activity)}
              >
                {activity.activityDisplayName}
              </Link>
            ) : (
              activity.activityDisplayName
            )}
          </Typography>

          {isVideo && (
            <Typography variant="caption" color="text.secondary">
              {length} min
            </Typography>
          )}
        </Box>
      </Box>

      {/* Points */}
      <Box
        display="flex"
        alignItems="center"
        minWidth={100}
        justifyContent="flex-end"
        gap={1.5}
      >
        {isCompleted ? (
          <CheckedCircleIcon variant="completed" width={18} height={18} />
        ) : (
          <CircleOutlineIcon width={16} height={16} />
        )}
        <Typography variant="body1" minWidth={35} textAlign="right">
          {activity.activityPoints}
        </Typography>
      </Box>
    </ListItem>
  );
};

export default CarePlanActivityItem;
